/**
 * @fileoverview Academy Configuration — Administration
 * Fee Plans, Coach Roles, License Levels, Age Groups — all stored in the database.
 */

import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";

import PropTypes from "prop-types";
import { academyService } from "api";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftBadge from "components/SoftBadge";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import FootballLoader from "components/FootballLoader";

// ── Fee Plan Section ───────────────────────────────────────────────────────────
function FeePlanSection({ plans, loading, onCreate, onDelete }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName]             = useState("");
  const [monthlyFee, setMonthlyFee] = useState("");
  const [admissionFee, setAdmissionFee] = useState("");
  const [subsidyPct, setSubsidyPct] = useState("0");
  const [description, setDescription] = useState("");
  const [saving, setSaving]         = useState(false);
  const [err, setErr]               = useState("");

  const reset = () => { setName(""); setMonthlyFee(""); setAdmissionFee(""); setSubsidyPct("0"); setDescription(""); setErr(""); };

  const handleAdd = async () => {
    const n = name.trim();
    if (!n) { setErr("Plan name is required."); return; }
    if (!monthlyFee || Number(monthlyFee) < 0) { setErr("Monthly fee is required."); return; }
    if (!admissionFee || Number(admissionFee) < 0) { setErr("Admission fee is required."); return; }
    const pct = Number(subsidyPct);
    if (isNaN(pct) || pct < 0 || pct > 100) { setErr("Subsidy must be 0–100."); return; }
    setSaving(true); setErr("");
    try {
      await onCreate({ name: n, baseMonthlyFee: Number(monthlyFee), baseAdmissionFee: Number(admissionFee), subsidyPct: pct, description: description.trim() || null, isActive: true, sortOrder: plans.length });
      setDialogOpen(false); reset();
    } catch (e) {
      setErr(e?.detail ?? "Failed to save plan.");
    } finally { setSaving(false); }
  };

  const BADGE_COLORS = ["info", "success", "warning", "error", "secondary"];

  return (
    <Card>
      <SoftBox p={2.5}>
        <SoftBox display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
          <SoftBox display="flex" alignItems="center" gap={1}>
            <Icon sx={{ color: "info.main", fontSize: "1.1rem" }}>payments</Icon>
            <SoftTypography variant="h6" fontWeight="bold" color="dark">Fee Plans</SoftTypography>
          </SoftBox>
          <SoftButton variant="gradient" color="info" size="small" startIcon={<Icon>add</Icon>}
            onClick={() => setDialogOpen(true)}>
            Add Plan
          </SoftButton>
        </SoftBox>
        <SoftTypography variant="caption" color="text" display="block" mb={1.5}>
          Define base fees and subsidy percentages. Players auto-calculate effective fees from their plan.
        </SoftTypography>

        {loading ? (
          <SoftBox py={3}><FootballLoader label="Loading plans…" /></SoftBox>
        ) : (
          <SoftBox sx={{ border: "1px solid #f0f0f0", borderRadius: 2, overflow: "hidden", mb: 1 }}>
            <SoftBox display="flex" px={2} py={1} sx={{ backgroundColor: "#fafafa", borderBottom: "1px solid #f0f0f0" }}>
              <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: 1 }}>PLAN NAME</SoftTypography>
              <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 110px", textAlign: "right" }}>BASE MONTHLY</SoftTypography>
              <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 110px", textAlign: "right" }}>BASE ADMISSION</SoftTypography>
              <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 80px", textAlign: "center" }}>SUBSIDY</SoftTypography>
              <SoftTypography variant="caption" fontWeight="bold" color="text" sx={{ flex: "0 0 110px", textAlign: "right" }}>EFF. MONTHLY</SoftTypography>
              <SoftBox sx={{ width: 36 }} />
            </SoftBox>
            {plans.map((plan, idx) => {
              const effMonthly   = plan.baseMonthlyFee   * (1 - plan.subsidyPct / 100);
              const effAdmission = plan.baseAdmissionFee * (1 - plan.subsidyPct / 100);
              return (
                <SoftBox key={plan.id} display="flex" alignItems="center" px={2} py={1.25}
                  sx={{ borderBottom: idx < plans.length - 1 ? "1px solid #f5f5f5" : "none",
                        "&:hover": { backgroundColor: "rgba(0,0,0,0.01)" } }}>
                  <SoftBox sx={{ flex: 1 }}>
                    <SoftTypography variant="button" fontWeight="medium" color="dark">{plan.name}</SoftTypography>
                    {plan.description && (
                      <SoftTypography variant="caption" color="text" display="block">{plan.description}</SoftTypography>
                    )}
                  </SoftBox>
                  <SoftTypography variant="caption" color="text" sx={{ flex: "0 0 110px", textAlign: "right" }}>
                    ₹{Number(plan.baseMonthlyFee).toLocaleString("en-IN")}
                  </SoftTypography>
                  <SoftTypography variant="caption" color="text" sx={{ flex: "0 0 110px", textAlign: "right" }}>
                    ₹{Number(plan.baseAdmissionFee).toLocaleString("en-IN")}
                    {plan.subsidyPct > 0 && (
                      <SoftTypography variant="caption" color="success" display="block">
                        → ₹{effAdmission.toLocaleString("en-IN")}
                      </SoftTypography>
                    )}
                  </SoftTypography>
                  <SoftBox sx={{ flex: "0 0 80px", textAlign: "center" }}>
                    {plan.subsidyPct > 0
                      ? <SoftBadge badgeContent={`${plan.subsidyPct}% off`} color={BADGE_COLORS[idx % BADGE_COLORS.length]} size="xs" container />
                      : <SoftTypography variant="caption" color="text">—</SoftTypography>}
                  </SoftBox>
                  <SoftTypography variant="caption" fontWeight="bold" color="dark" sx={{ flex: "0 0 110px", textAlign: "right" }}>
                    ₹{effMonthly.toLocaleString("en-IN")}
                  </SoftTypography>
                  <IconButton size="small" onClick={() => onDelete(plan.id)} sx={{ width: 28, height: 28 }}>
                    <Icon sx={{ fontSize: "0.9rem", color: "error.light" }}>delete</Icon>
                  </IconButton>
                </SoftBox>
              );
            })}
            {plans.length === 0 && (
              <SoftBox p={2} textAlign="center">
                <SoftTypography variant="caption" color="text" sx={{ fontStyle: "italic" }}>No plans yet — add one above</SoftTypography>
              </SoftBox>
            )}
          </SoftBox>
        )}
      </SoftBox>

      {/* Add plan dialog */}
      <Dialog open={dialogOpen} onClose={() => { setDialogOpen(false); reset(); }} maxWidth="xs" fullWidth>
        <DialogTitle>Add Fee Plan</DialogTitle>
        <DialogContent>
          {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth required size="small" label="Plan Name" value={name}
                onChange={(e) => { setName(e.target.value); setErr(""); }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth required size="small" label="Base Monthly Fee (₹)" type="number"
                value={monthlyFee} onChange={(e) => { setMonthlyFee(e.target.value); setErr(""); }}
                inputProps={{ min: 0 }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth required size="small" label="Base Admission Fee (₹)" type="number"
                value={admissionFee} onChange={(e) => { setAdmissionFee(e.target.value); setErr(""); }}
                inputProps={{ min: 0 }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Subsidy %" type="number"
                value={subsidyPct} onChange={(e) => { setSubsidyPct(e.target.value); setErr(""); }}
                inputProps={{ min: 0, max: 100 }} />
            </Grid>
            {subsidyPct > 0 && monthlyFee && (
              <Grid item xs={6}>
                <SoftBox sx={{ backgroundColor: "#e8f5e9", borderRadius: 1, p: 1.25 }}>
                  <SoftTypography variant="caption" color="text" display="block">Effective Monthly</SoftTypography>
                  <SoftTypography variant="button" fontWeight="bold" color="dark">
                    ₹{(Number(monthlyFee) * (1 - Number(subsidyPct) / 100)).toLocaleString("en-IN")}
                  </SoftTypography>
                </SoftBox>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Description (optional)" value={description}
                onChange={(e) => setDescription(e.target.value)} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <SoftButton variant="text" color="secondary" onClick={() => { setDialogOpen(false); reset(); }}>Cancel</SoftButton>
          <SoftButton variant="gradient" color="info" onClick={handleAdd} disabled={saving}>
            {saving ? "Saving…" : "Add Plan"}
          </SoftButton>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
FeePlanSection.propTypes = {
  plans: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  onCreate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

// ── License Level Section ──────────────────────────────────────────────────────
function LicenseLevelSection({ levels, loading, onSave }) {
  const [input, setInput]   = useState("");
  const [err, setErr]       = useState("");
  const [saving, setSaving] = useState(false);
  const [local, setLocal]   = useState([]);

  useEffect(() => { setLocal(levels); }, [levels]);

  const handleAdd = () => {
    const val = input.trim().toUpperCase();
    if (!val) { setErr("Value cannot be empty."); return; }
    if (local.some((l) => l.level.toUpperCase() === val)) { setErr("Already exists."); return; }
    setErr(""); setInput("");
    setLocal((prev) => [...prev, { level: val, label: val, sortOrder: prev.length }]);
  };

  const handleDelete = (level) => setLocal((prev) => prev.filter((l) => l.level !== level));

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(local.map((l) => l.level));
    } finally { setSaving(false); }
  };

  return (
    <Card sx={{ height: "100%" }}>
      <SoftBox p={2.5}>
        <SoftBox display="flex" alignItems="center" gap={1} mb={0.5}>
          <Icon sx={{ color: "info.main", fontSize: "1.1rem" }}>verified</Icon>
          <SoftTypography variant="h6" fontWeight="bold" color="dark">License Levels</SoftTypography>
        </SoftBox>
        <SoftTypography variant="caption" color="text" display="block" mb={1.5}>
          License options in the Coach form (saved to database)
        </SoftTypography>

        {loading ? (
          <SoftBox py={2}><FootballLoader label="Loading…" /></SoftBox>
        ) : (
          <SoftBox display="flex" flexWrap="wrap" gap={1} mb={2} minHeight={32}>
            {local.map((item) => (
              <Chip key={item.level} label={item.label ?? item.level}
                onDelete={() => handleDelete(item.level)}
                size="small"
                sx={{
                  fontWeight: 500, backgroundColor: "rgba(26,115,232,0.08)", color: "#344767",
                  "& .MuiChip-deleteIcon": { color: "#7b809a", fontSize: "0.9rem" },
                }} />
            ))}
            {local.length === 0 && (
              <SoftTypography variant="caption" color="text" sx={{ fontStyle: "italic" }}>No levels yet</SoftTypography>
            )}
          </SoftBox>
        )}

        <SoftBox display="flex" gap={1} alignItems="flex-start" mb={1.5}>
          <TextField size="small" fullWidth placeholder="Add new level…" value={input}
            onChange={(e) => { setInput(e.target.value); setErr(""); }}
            onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            error={Boolean(err)} helperText={err} />
          <SoftButton variant="gradient" color="info" size="small" onClick={handleAdd}
            sx={{ minWidth: 0, px: 2, flexShrink: 0 }}>
            <Icon>add</Icon>
          </SoftButton>
        </SoftBox>

        <SoftButton variant="outlined" color="info" size="small" fullWidth onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save License Levels"}
        </SoftButton>
      </SoftBox>
    </Card>
  );
}
LicenseLevelSection.propTypes = {
  levels: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
};

// ── Generic editable list section ─────────────────────────────────────────────
function ListSection({ title, icon, items, onAdd, onDelete, description }) {
  const [input, setInput] = useState("");
  const [err, setErr]     = useState("");

  const handleAdd = () => {
    const val = input.trim();
    if (!val) { setErr("Value cannot be empty."); return; }
    if (items.some((i) => (typeof i === "string" ? i : i.value) === val)) { setErr("Already exists."); return; }
    setErr(""); setInput(""); onAdd(val);
  };

  return (
    <Card sx={{ height: "100%" }}>
      <SoftBox p={2.5}>
        <SoftBox display="flex" alignItems="center" gap={1} mb={0.5}>
          <Icon sx={{ color: "info.main", fontSize: "1.1rem" }}>{icon}</Icon>
          <SoftTypography variant="h6" fontWeight="bold" color="dark">{title}</SoftTypography>
        </SoftBox>
        {description && (
          <SoftTypography variant="caption" color="text" display="block" mb={1.5}>{description}</SoftTypography>
        )}
        <SoftBox display="flex" flexWrap="wrap" gap={1} mb={2} minHeight={32}>
          {items.map((item) => {
            const label = typeof item === "string" ? item : item.label;
            const key   = typeof item === "string" ? item : item.value;
            return (
              <Chip key={key} label={label} onDelete={() => onDelete(key)} size="small"
                sx={{
                  fontWeight: 500, backgroundColor: "rgba(26,115,232,0.08)", color: "#344767",
                  "& .MuiChip-deleteIcon": { color: "#7b809a", fontSize: "0.9rem" },
                }} />
            );
          })}
          {items.length === 0 && (
            <SoftTypography variant="caption" color="text" sx={{ fontStyle: "italic" }}>No items yet</SoftTypography>
          )}
        </SoftBox>
        <SoftBox display="flex" gap={1} alignItems="flex-start">
          <TextField size="small" fullWidth placeholder={`Add new ${title.toLowerCase().replace(/s$/, "")}…`}
            value={input} onChange={(e) => { setInput(e.target.value); setErr(""); }}
            onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            error={Boolean(err)} helperText={err} />
          <SoftButton variant="gradient" color="info" size="small" onClick={handleAdd}
            sx={{ minWidth: 0, px: 2, flexShrink: 0 }}>
            <Icon>add</Icon>
          </SoftButton>
        </SoftBox>
      </SoftBox>
    </Card>
  );
}
ListSection.propTypes = {
  title: PropTypes.string.isRequired, icon: PropTypes.string.isRequired,
  items: PropTypes.array.isRequired, onAdd: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired, description: PropTypes.string,
};

// ── Main ──────────────────────────────────────────────────────────────────────
function AcademyConfig() {
  const [feePlans, setFeePlans]             = useState([]);
  const [licenseLevels, setLicenseLevels]   = useState([]);
  const [coachRoles, setCoachRoles]         = useState([]);
  const [ageGroups, setAgeGroups]           = useState([]);
  const [trainingDays, setTrainingDays]     = useState([]);
  const [timeSlots, setTimeSlots]           = useState([]);

  const [loadingPlans, setLoadingPlans]     = useState(true);
  const [loadingLevels, setLoadingLevels]   = useState(true);
  const [saved, setSaved]                   = useState(false);
  const [snackMsg, setSnackMsg]             = useState("");

  useEffect(() => {
    setLoadingPlans(true);
    academyService.getFeePlans().then((d) => setFeePlans(Array.isArray(d) ? d : [])).finally(() => setLoadingPlans(false));

    setLoadingLevels(true);
    academyService.getLicenseLevels().then((d) => setLicenseLevels(Array.isArray(d) ? d : [])).finally(() => setLoadingLevels(false));

    Promise.all([
      academyService.getAgeGroups().catch(() => null),
      academyService.getTrainingDaySchedules().catch(() => null),
      academyService.getCoachRoles().catch(() => null),
      academyService.getTimeSlots().catch(() => null),
    ]).then(([ag, td, roles, ts]) => {
      if (Array.isArray(ag))    setAgeGroups(ag);
      if (Array.isArray(td))    setTrainingDays(td);
      if (Array.isArray(roles)) setCoachRoles(roles);
      if (Array.isArray(ts))    setTimeSlots(ts);
    });
  }, []);

  const flash = (msg) => { setSnackMsg(msg); setSaved(true); setTimeout(() => setSaved(false), 2500); };

  // Fee plan handlers
  const handleCreatePlan = async (data) => {
    const created = await academyService.createFeePlan(data);
    setFeePlans((prev) => [...prev, created]);
    flash("Fee plan added.");
  };
  const handleDeletePlan = async (id) => {
    await academyService.deleteFeePlan(id);
    setFeePlans((prev) => prev.filter((p) => p.id !== id));
    flash("Fee plan deleted.");
  };

  // License level handler
  const handleSaveLicenseLevels = async (levels) => {
    await academyService.saveLicenseLevels(levels);
    const refreshed = await academyService.getLicenseLevels();
    setLicenseLevels(Array.isArray(refreshed) ? refreshed : []);
    flash("License levels saved.");
  };

  // Generic config save
  const handleSaveConfig = async () => {
    await Promise.all([
      academyService.saveAgeGroups(ageGroups).catch(() => {}),
      academyService.saveTrainingDaySchedules(trainingDays).catch(() => {}),
      academyService.saveCoachRoles(coachRoles).catch(() => {}),
      academyService.saveTimeSlots(timeSlots).catch(() => {}),
    ]);
    flash("Configuration saved.");
  };

  const addItem  = (setter) => (val) => setter((prev) => [...prev, val]);
  const delItem  = (setter) => (val) => setter((prev) => prev.filter((x) => x !== val));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>

        <SoftBox mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <SoftBox>
            <SoftTypography variant="h4" fontWeight="bold" color="dark">Academy Configuration</SoftTypography>
            <SoftTypography variant="body2" color="text">
              Manage fee plans, coach roles, license levels and age groups
            </SoftTypography>
          </SoftBox>
          <SoftBox display="flex" gap={1} alignItems="center">
            {saved && (
              <SoftBox display="flex" alignItems="center" gap={0.5}>
                <Icon sx={{ color: "success.main", fontSize: "1rem" }}>check_circle</Icon>
                <SoftTypography variant="caption" color="success">{snackMsg || "Saved"}</SoftTypography>
              </SoftBox>
            )}
            <SoftButton variant="gradient" color="info" startIcon={<Icon>save</Icon>} onClick={handleSaveConfig}>
              Save Config
            </SoftButton>
          </SoftBox>
        </SoftBox>

        {/* Fee Plans — full width */}
        <SoftBox mb={3}>
          <FeePlanSection
            plans={feePlans} loading={loadingPlans}
            onCreate={handleCreatePlan} onDelete={handleDeletePlan} />
        </SoftBox>

        {/* Roles + Licenses + Age Groups */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={4}>
            <ListSection
              title="Coach Roles" icon="manage_accounts"
              description="Roles available in the Coach form (saved to database)"
              items={coachRoles}
              onAdd={addItem(setCoachRoles)} onDelete={delItem(setCoachRoles)} />
          </Grid>
          <Grid item xs={12} md={4}>
            <LicenseLevelSection levels={licenseLevels} loading={loadingLevels} onSave={handleSaveLicenseLevels} />
          </Grid>
          <Grid item xs={12} md={4}>
            <ListSection
              title="Age Groups" icon="people"
              description="Age groups used for Batches and Players"
              items={ageGroups}
              onAdd={addItem(setAgeGroups)} onDelete={delItem(setAgeGroups)} />
          </Grid>
        </Grid>

        {/* Training Days + Time Slots */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={6}>
            <ListSection
              title="Training Day Schedules" icon="event"
              description="Schedule options shown in the Batch creation dropdown (e.g. Sat-Sun)"
              items={trainingDays}
              onAdd={addItem(setTrainingDays)} onDelete={delItem(setTrainingDays)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <ListSection
              title="Time Slots" icon="schedule"
              description="Time slot options shown in the Batch creation dropdown (e.g. 06:00-08:00)"
              items={timeSlots}
              onAdd={addItem(setTimeSlots)} onDelete={delItem(setTimeSlots)} />
          </Grid>
        </Grid>

      </SoftBox>
      <Footer company={{ href: "#", name: "Mohammedan SC" }} links={[]} />
    </DashboardLayout>
  );
}

export default AcademyConfig;
